import { useCallback, useEffect, useRef, useState } from 'react';

import { EMPTY_VALUE } from '../../constants';
import { useConfig } from '../../contexts/config-context';
import {
  FORM_STATUS,
  useFormStatus,
} from '../../contexts/form-status-provider';
import { type WixFile } from '../form/types';

import { useUploadSignature } from './use-upload-signature';
import { convertToPNGFile, decodeDataUrl, isDataUrl } from './utils/data-url';

interface UseSignatureArgs {
  value?: WixFile[] | null;
  onChange: (value: WixFile[] | undefined | null) => void;
  target: string;
}

export const useSignatureValue = ({
  value,
  onChange,
  target,
}: UseSignatureArgs) => {
  const [signatureValue, setSignatureValue] = useState(value);
  const { isRTL: rtl } = useConfig();
  const uploadSignatureFile = useUploadSignature(target);
  const { setFormStatus } = useFormStatus();
  const uploadTimeoutIdRef = useRef<NodeJS.Timeout | null>();

  useEffect(() => {
    if (value === EMPTY_VALUE) {
      setSignatureValue(EMPTY_VALUE);
    }
  }, [value]);

  const cancelUpload = useCallback(() => {
    if (uploadTimeoutIdRef.current) {
      clearTimeout(uploadTimeoutIdRef.current);
      uploadTimeoutIdRef.current = null;
    }
  }, []);

  const signature = signatureValue?.[0];
  const decodedValue = signature?.url
    ? decodeDataUrl(signature.url)
    : undefined;

  const uploadSignature = useCallback(
    async (_signature: WixFile | null) => {
      try {
        const dataUrl = _signature?.url ?? '';
        if (isDataUrl(dataUrl)) {
          const signatureFile = await convertToPNGFile(dataUrl, rtl);
          if (signatureFile) {
            const uploadedValue = await uploadSignatureFile(signatureFile);
            onChange([uploadedValue]);
          } else {
            // The signature is empty ( example the user choose a fully transparent image )
            setSignatureValue(undefined);
            onChange(undefined);
          }
        }
        uploadTimeoutIdRef.current = null;
      } finally {
        setFormStatus(target, FORM_STATUS.READY);
      }
    },
    [setFormStatus, target, rtl, uploadSignatureFile, onChange],
  );

  const handleChange = useCallback(
    (newSignature: WixFile | null) => {
      const deferUploadSignature = (_signature: WixFile | null) => {
        uploadTimeoutIdRef.current = setTimeout(
          () => uploadSignature(_signature),
          1_500,
        );
      };

      if (newSignature) {
        if (uploadTimeoutIdRef.current) {
          clearTimeout(uploadTimeoutIdRef.current);
          uploadTimeoutIdRef.current = null;
        }
        setSignatureValue([newSignature]);
        deferUploadSignature(newSignature);
      } else {
        onChange?.(null);
        setSignatureValue(undefined);
        setFormStatus(target, FORM_STATUS.READY);
      }
    },
    [uploadSignature, onChange, setFormStatus, target],
  );

  return {
    handleChange,
    signature,
    decodedValue,
    cancelUpload,
  };
};
